import { useState, type FormEvent } from "react";

import type {
  Event,
  MeetpointEventSummary,
  Stage
} from "@attendik/types/admin-schemas";

import { api, type SnapshotRecipient } from "../../api/client.js";
import { isApiError } from "../../api/errors.js";
import { Badge } from "../../components/ui/badge.js";
import { Button } from "../../components/ui/button.js";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from "../../components/ui/card.js";
import {
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader
} from "../../components/ui/dialog.js";
import { Input } from "../../components/ui/input.js";
import { Label } from "../../components/ui/label.js";
import { Select } from "../../components/ui/select.js";
import {
  formatDateTime,
  fromDateTimeLocal,
  toDateTimeLocal
} from "../../lib/format.js";
import { useAsync } from "../../lib/useAsync.js";
import { PerStageStrip } from "../shared/utilisation.js";

/**
 * Overview tab — metric strip, schedule edit, webhook card (build plan P2-T8a).
 */
export function OverviewTab({
  event,
  onUpdated
}: {
  event: Event;
  onUpdated: () => void;
}): JSX.Element {
  const attendees = useAsync(() => api.listAttendees(event.id, { limit: 1 }), [event.id]);
  const operators = useAsync(() => api.listEventOperators(event.id), [event.id]);
  // Stages are loaded for the MonitorCard's per-stage dropdown. The per-stage
  // strip itself now reads from liveCounts.per_stage instead (admin CR-01
  // follow-up — drives the amber@80%/red@100% utilisation colour band).
  const stages = useAsync(() => api.listStages(event.id), [event.id]);
  const liveCounts = useAsync(() => api.getEventLiveCounts(event.id), [event.id]);

  // CR-06 §4.2: the Pre-fill from Meetpoint button is only meaningful when
  // MP integration is configured for this event. A 404 from getMeetpointConfig
  // is the "not configured" state.
  const mpConfigured = useAsync(async (): Promise<boolean> => {
    try {
      await api.getMeetpointConfig(event.id);
      return true;
    } catch (err) {
      if (isApiError(err) && err.status === 404) {
        return false;
      }
      throw err;
    }
  }, [event.id]);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Metric label="Registered" value={attendees.data?.total ?? "—"} />
        <Metric label="Operators" value={operators.data?.length ?? "—"} />
        <Metric label="Capacity" value={event.capacity ?? "—"} />
        <Metric label="Status" value={event.status} />
      </div>

      <LiveStatusCard
        event={event}
        currentlyIn={liveCounts.data?.currently_in ?? null}
        onChanged={() => {
          onUpdated();
          liveCounts.reload();
        }}
      />

      {liveCounts.data && liveCounts.data.per_stage.length > 0 ? (
        <PerStageStrip perStage={liveCounts.data.per_stage} />
      ) : null}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ScheduleCard
          event={event}
          onUpdated={onUpdated}
          showMeetpointPrefill={mpConfigured.data === true}
        />
        <WebhookCard event={event} onUpdated={onUpdated} />
      </div>

      <SnapshotRecipientsCard event={event} />

      <MonitorCard event={event} stages={stages.data ?? []} />
    </div>
  );
}

/**
 * CR-11. Live-status card: shows whether the event is in test mode or live,
 * the current in-room count, and the go-live control. Going live stamps
 * `went_live_at = now()`, which resets every live counter to 0 (counters count
 * only scans at/after the marker) without deleting any scan — it's a moving
 * cutoff, so pressing it again re-zeroes from the new instant.
 *
 * The control is graduated (Risk 1): in test mode it's a primary action with a
 * plain confirm; once live, re-pressing and "back to test mode" require an
 * explicit acknowledgement because they disrupt a running event (everyone in
 * the room must re-scan).
 */
function LiveStatusCard({
  event,
  currentlyIn,
  onChanged
}: {
  event: Event;
  currentlyIn: number | null;
  onChanged: () => void;
}): JSX.Element {
  const isLive = event.went_live_at !== null;
  const [dialog, setDialog] = useState<null | "go-live" | "reset" | "test-mode">(
    null
  );
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = (): void => {
    setDialog(null);
    setAck(false);
    setError(null);
  };

  const run = async (
    action: "go-live" | "reset" | "test-mode"
  ): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      if (action === "test-mode") {
        await api.updateEvent(event.id, { went_live_at: null });
      } else {
        await api.goLive(event.id);
      }
      close();
      onChanged();
    } catch (err) {
      setError(isApiError(err) ? err.message : "Could not update the event.");
    } finally {
      setBusy(false);
    }
  };

  // The disruptive actions (reset while live, back to test mode) gate the
  // confirm button behind an explicit acknowledgement.
  const danger = dialog === "reset" || dialog === "test-mode";

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle>Live status</CardTitle>
        <Badge variant={isLive ? "success" : "outline"}>
          {isLive ? "Live" : "Test mode"}
        </Badge>
      </CardHeader>
      <CardContent className="space-y-3 pt-0 text-sm">
        {isLive ? (
          <p className="text-muted-foreground">
            Counting since{" "}
            <strong>{formatDateTime(event.went_live_at as string)}</strong>
            {currentlyIn !== null ? (
              <>
                {" "}
                · <strong>{currentlyIn}</strong> currently in room
              </>
            ) : null}
            . Earlier test scans stay in the log but are excluded from the
            counts.
          </p>
        ) : (
          <p className="text-muted-foreground">
            This event is in <strong>test mode</strong>: every scan counts, so
            you can verify scanning and the monitor. Click{" "}
            <strong>Go live</strong> when the real event starts — counters reset
            to 0 and only scans from that moment count. Nothing is deleted.
          </p>
        )}
        <div className="flex flex-wrap items-center gap-2">
          {!isLive ? (
            <Button onClick={() => setDialog("go-live")} data-testid="go-live">
              Go live (reset counters)
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => setDialog("reset")}
                data-testid="go-live-again"
              >
                Reset counts again
              </Button>
              <Button
                variant="outline"
                onClick={() => setDialog("test-mode")}
                data-testid="back-to-test"
              >
                Back to test mode
              </Button>
            </>
          )}
        </div>
      </CardContent>

      {dialog ? (
        <Dialog open onClose={close} labelledBy="live-status-title">
          <DialogHeader
            id="live-status-title"
            title={
              dialog === "go-live"
                ? "Go live?"
                : dialog === "reset"
                  ? "Reset counts again?"
                  : "Back to test mode?"
            }
            onClose={close}
          />
          <DialogBody>
            <p className="text-sm text-muted-foreground">
              {dialog === "go-live" ? (
                <>
                  Counters reset to 0 and only scans from now on are counted.
                  Test scans remain in the scan log. You can do this again
                  later.
                </>
              ) : dialog === "reset" ? (
                <>
                  This event is already live since{" "}
                  <strong>{formatDateTime(event.went_live_at as string)}</strong>
                  . Resetting again zeroes every counter and removes everyone
                  from the in-room count — they must re-scan to be counted.
                  Nothing is deleted.
                </>
              ) : (
                <>
                  Returning to test mode makes <em>all</em> scans count again,
                  including the earlier test scans. The live counts will jump up
                  accordingly.
                </>
              )}
            </p>
            {danger ? (
              <label className="mt-3 flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={ack}
                  onChange={(e) => setAck(e.target.checked)}
                  data-testid="live-status-ack"
                />
                I understand this affects the live event.
              </label>
            ) : null}
            {error ? (
              <p role="alert" className="mt-2 text-sm text-destructive">
                {error}
              </p>
            ) : null}
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={close} disabled={busy}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                void run(dialog);
              }}
              disabled={busy || (danger && !ack)}
              data-testid="live-status-confirm"
            >
              {busy
                ? "Working…"
                : dialog === "test-mode"
                  ? "Back to test mode"
                  : dialog === "reset"
                    ? "Reset counts"
                    : "Go live"}
            </Button>
          </DialogFooter>
        </Dialog>
      ) : null}
    </Card>
  );
}

/**
 * Monitor card — fetches the HMAC-signed monitor URL on demand and offers
 * "Copy" and "Open in new tab" actions. The URL is the only place an
 * admin sees the token; it is not displayed by default. When the event has
 * stages, a per-stage dropdown is rendered next to the event-level buttons
 * (admin CR-01 §3.6) so an admin can copy or open the per-stage URL too.
 */
function MonitorCard({
  event,
  stages
}: {
  event: Event;
  stages: Stage[];
}): JSX.Element {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedStageId, setSelectedStageId] = useState("");
  const [stageBusy, setStageBusy] = useState(false);

  const ensureUrl = async (): Promise<string | null> => {
    if (url) {
      return url;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await api.getEventMonitorLink(event.id);
      setUrl(result.url);
      return result.url;
    } catch (err) {
      setError(isApiError(err) ? err.message : "Could not generate the link.");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (): Promise<void> => {
    const link = await ensureUrl();
    if (!link) {
      return;
    }
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError("Could not access the clipboard.");
    }
  };

  const handleOpen = async (): Promise<void> => {
    const link = await ensureUrl();
    if (!link) {
      return;
    }
    window.open(link, "_blank", "noopener,noreferrer");
  };

  const handleStageAction = async (action: "copy" | "open"): Promise<void> => {
    if (!selectedStageId) {
      return;
    }
    setError(null);
    setStageBusy(true);
    try {
      const result = await api.getStageMonitorLink(selectedStageId);
      if (action === "copy") {
        await navigator.clipboard.writeText(result.url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        window.open(result.url, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      setError(
        isApiError(err) ? err.message : "Could not generate the stage link."
      );
    } finally {
      setStageBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Monitor</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 pt-0 text-sm">
        <p className="text-muted-foreground">
          A read-only live board for the foyer screen. The link is signed —
          anyone with it can view counts for this event.
        </p>
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
        {copied ? (
          <p className="text-sm text-emerald-700" role="status">
            Copied
          </p>
        ) : null}
        <div className="flex gap-2">
          <Button
            type="button"
            size="sm"
            onClick={() => {
              void handleCopy();
            }}
            disabled={loading}
            data-testid="monitor-copy"
          >
            {loading ? "Working…" : "Copy monitor link"}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              void handleOpen();
            }}
            disabled={loading}
            data-testid="monitor-open"
          >
            Open in new tab
          </Button>
        </div>

        {stages.length > 0 ? (
          <div className="border-t border-border pt-3">
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Per-stage monitor link
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Select
                value={selectedStageId}
                onChange={(e) => setSelectedStageId(e.target.value)}
                data-testid="stage-monitor-select"
                aria-label="Select a stage"
              >
                <option value="">Select a stage…</option>
                {stages.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
              <Button
                type="button"
                size="sm"
                onClick={() => {
                  void handleStageAction("copy");
                }}
                disabled={!selectedStageId || stageBusy}
                data-testid="stage-monitor-copy"
              >
                {stageBusy ? "Working…" : "Copy"}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  void handleStageAction("open");
                }}
                disabled={!selectedStageId || stageBusy}
                data-testid="stage-monitor-open"
              >
                Open
              </Button>
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function Metric({
  label,
  value
}: {
  label: string;
  value: string | number;
}): JSX.Element {
  return (
    <Card>
      <CardContent className="pt-6">
        <p className="text-sm text-muted-foreground">{label}</p>
        <p className="mt-1 text-2xl font-semibold tracking-tight">{value}</p>
      </CardContent>
    </Card>
  );
}

function ScheduleCard({
  event,
  onUpdated,
  showMeetpointPrefill
}: {
  event: Event;
  onUpdated: () => void;
  showMeetpointPrefill: boolean;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(event.name);
  const [activeFrom, setActiveFrom] = useState(toDateTimeLocal(event.active_from));
  const [activeTo, setActiveTo] = useState(toDateTimeLocal(event.active_to));
  const [capacity, setCapacity] = useState(
    event.capacity != null ? String(event.capacity) : ""
  );
  const [status, setStatus] = useState<Event["status"]>(event.status);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // CR-06 §4.2 — pre-fill flow state.
  const [prefillBusy, setPrefillBusy] = useState(false);
  const [prefillError, setPrefillError] = useState<string | null>(null);
  const [prefillSummary, setPrefillSummary] =
    useState<MeetpointEventSummary | null>(null);

  const reset = (): void => {
    setName(event.name);
    setActiveFrom(toDateTimeLocal(event.active_from));
    setActiveTo(toDateTimeLocal(event.active_to));
    setCapacity(event.capacity != null ? String(event.capacity) : "");
    setStatus(event.status);
    setError(null);
  };

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (new Date(activeTo) <= new Date(activeFrom)) {
      setError("The end time must be after the start time.");
      return;
    }
    setSaving(true);
    try {
      await api.updateEvent(event.id, {
        name: name.trim(),
        active_from: fromDateTimeLocal(activeFrom),
        active_to: fromDateTimeLocal(activeTo),
        capacity: capacity ? Number(capacity) : null,
        status
      });
      setEditing(false);
      onUpdated();
    } catch (err) {
      setError(isApiError(err) ? err.message : "Could not save changes.");
    } finally {
      setSaving(false);
    }
  };

  const handlePrefill = async (): Promise<void> => {
    setPrefillError(null);
    setPrefillBusy(true);
    try {
      const summary = await api.getMeetpointEventSummary(event.id);
      setPrefillSummary(summary);
    } catch (err) {
      setPrefillError(
        isApiError(err) ? err.message : "Could not fetch the Meetpoint summary."
      );
    } finally {
      setPrefillBusy(false);
    }
  };

  const handleApplyPrefill = async (): Promise<void> => {
    if (!prefillSummary) {
      return;
    }
    setPrefillBusy(true);
    setPrefillError(null);
    try {
      await api.applyMeetpointSummary(event.id);
      setPrefillSummary(null);
      onUpdated();
    } catch (err) {
      setPrefillError(
        isApiError(err) ? err.message : "Could not apply the Meetpoint summary."
      );
    } finally {
      setPrefillBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle>Schedule</CardTitle>
        <div className="flex items-center gap-2">
          {showMeetpointPrefill && !editing ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                void handlePrefill();
              }}
              disabled={prefillBusy}
              data-testid="schedule-prefill-meetpoint"
            >
              {prefillBusy ? "Fetching…" : "Pre-fill from Meetpoint"}
            </Button>
          ) : null}
          {!editing ? (
            <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
              Edit
            </Button>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {!editing ? (
          <dl className="space-y-3 text-sm">
            <Row label="Name" value={event.name} />
            <Row label="Active from" value={formatDateTime(event.active_from)} />
            <Row label="Active to" value={formatDateTime(event.active_to)} />
            <Row label="Capacity" value={event.capacity != null ? String(event.capacity) : "—"} />
            <Row label="Status" value={event.status} />
          </dl>
        ) : (
          <form className="space-y-3" onSubmit={handleSubmit}>
            <div className="space-y-1.5">
              <Label htmlFor="ov-name">Name</Label>
              <Input id="ov-name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="ov-from">Active from</Label>
                <Input
                  id="ov-from"
                  type="datetime-local"
                  value={activeFrom}
                  onChange={(e) => setActiveFrom(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ov-to">Active to</Label>
                <Input
                  id="ov-to"
                  type="datetime-local"
                  value={activeTo}
                  onChange={(e) => setActiveTo(e.target.value)}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="ov-capacity">Capacity</Label>
                <Input
                  id="ov-capacity"
                  type="number"
                  min={0}
                  value={capacity}
                  onChange={(e) => setCapacity(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ov-status">Status</Label>
                <Select
                  id="ov-status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value as Event["status"])}
                >
                  <option value="ACTIVE">Active</option>
                  <option value="CLOSED">Closed</option>
                </Select>
              </div>
            </div>
            {error ? (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  reset();
                  setEditing(false);
                }}
              >
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={saving}>
                {saving ? "Saving…" : "Save"}
              </Button>
            </div>
          </form>
        )}
        {prefillError ? (
          <p role="alert" className="mt-3 text-sm text-destructive">
            {prefillError}
          </p>
        ) : null}
      </CardContent>
      {prefillSummary ? (
        <Dialog
          open
          onClose={() => setPrefillSummary(null)}
          labelledBy="prefill-meetpoint-title"
        >
          <DialogHeader
            id="prefill-meetpoint-title"
            title="Pre-fill from Meetpoint"
            description="These values come from Meetpoint. Accept to overwrite the event."
            onClose={() => setPrefillSummary(null)}
          />
          <DialogBody>
            <dl className="space-y-2 text-sm">
              <Row label="Name" value={prefillSummary.event_name} />
              <Row
                label="Active from"
                value={
                  prefillSummary.active_from
                    ? formatDateTime(prefillSummary.active_from)
                    : "—"
                }
              />
              <Row
                label="Active to"
                value={
                  prefillSummary.active_to
                    ? formatDateTime(prefillSummary.active_to)
                    : "—"
                }
              />
            </dl>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPrefillSummary(null)}
              disabled={prefillBusy}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                void handleApplyPrefill();
              }}
              disabled={prefillBusy}
              data-testid="schedule-prefill-accept"
            >
              {prefillBusy ? "Applying…" : "Accept"}
            </Button>
          </DialogFooter>
        </Dialog>
      ) : null}
    </Card>
  );
}

function WebhookCard({
  event,
  onUpdated
}: {
  event: Event;
  onUpdated: () => void;
}): JSX.Element {
  const [editingUrl, setEditingUrl] = useState(false);
  const [url, setUrl] = useState(event.webhook_url ?? "");
  const [rotatedSecret, setRotatedSecret] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const saveUrl = async (): Promise<void> => {
    setError(null);
    setBusy(true);
    try {
      await api.updateEvent(event.id, { webhook_url: url.trim() || null });
      setEditingUrl(false);
      onUpdated();
    } catch (err) {
      setError(isApiError(err) ? err.message : "Could not save the webhook URL.");
    } finally {
      setBusy(false);
    }
  };

  const rotate = async (): Promise<void> => {
    setError(null);
    setBusy(true);
    try {
      const result = await api.rotateWebhookSecret(event.id);
      setRotatedSecret(result.webhook_secret);
    } catch (err) {
      setError(isApiError(err) ? err.message : "Could not rotate the secret.");
    } finally {
      setBusy(false);
    }
  };

  const testPing = async (): Promise<void> => {
    setError(null);
    setTestResult(null);
    setBusy(true);
    try {
      const result = await api.testWebhook(event.id);
      setTestResult(`Webhook reached — upstream returned HTTP ${result.status_code}.`);
    } catch (err) {
      if (isApiError(err) && err.code === "NO_WEBHOOK_URL") {
        setError("Configure a webhook URL before testing.");
      } else {
        setError(
          isApiError(err) ? err.message : "Webhook delivery failed."
        );
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Webhook</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 pt-0 text-sm">
        <div className="space-y-1.5">
          <Label htmlFor="wh-url">Live counts URL</Label>
          {!editingUrl ? (
            <div className="flex items-center justify-between gap-3">
              <span className="break-all text-muted-foreground">
                {event.webhook_url ?? "Not configured"}
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setEditingUrl(true)}
              >
                Edit
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Input
                id="wh-url"
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://monitor.example/hook"
              />
              <Button size="sm" onClick={saveUrl} disabled={busy}>
                Save
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setUrl(event.webhook_url ?? "");
                  setEditingUrl(false);
                }}
              >
                Cancel
              </Button>
            </div>
          )}
        </div>

        <div className="space-y-1.5">
          <Label>Signing secret</Label>
          {rotatedSecret ? (
            <div className="rounded-md border border-accent/40 bg-accent/5 p-3">
              <p className="text-xs text-muted-foreground">
                New secret — copy it now, it is shown only once:
              </p>
              <code className="mt-1 block break-all font-mono text-xs">
                {rotatedSecret}
              </code>
            </div>
          ) : (
            <p className="text-muted-foreground">
              ••••••••••••••••  (hidden — rotate to reveal a new one)
            </p>
          )}
        </div>

        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
        {testResult ? (
          <p className="text-sm text-emerald-700">{testResult}</p>
        ) : null}

        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={rotate} disabled={busy}>
            Rotate secret
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={testPing}
            disabled={busy || !event.webhook_url}
          >
            Send test ping
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-4">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right">{value}</dd>
    </div>
  );
}

/**
 * Snapshot recipients (Monitor CR-02). Manages the per-event list of email
 * addresses that receive the monitor "Snapshot" PDF. Per-event rather than
 * per-customer so a customer admin with many events doesn't end up with
 * every event's snapshot fanning out to every admin in the customer.
 */
function SnapshotRecipientsCard({ event }: { event: Event }): JSX.Element {
  const initial = useAsync(() => api.listSnapshotRecipients(event.id), [event.id]);
  const [emails, setEmails] = useState<string[]>([]);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [hydrated, setHydrated] = useState(false);

  // Hydrate the editable list once initial data arrives.
  if (!hydrated && initial.data) {
    setEmails(initial.data.data.map((r: SnapshotRecipient) => r.email));
    setHydrated(true);
  }

  const dirty =
    hydrated &&
    JSON.stringify(emails.map((e) => e.toLowerCase()).sort()) !==
      JSON.stringify(
        (initial.data?.data ?? [])
          .map((r: SnapshotRecipient) => r.email.toLowerCase())
          .sort()
      );

  const addDraft = (): void => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    if (emails.some((e) => e.toLowerCase() === trimmed.toLowerCase())) {
      setDraft("");
      return;
    }
    setEmails([...emails, trimmed]);
    setDraft("");
  };

  const removeAt = (i: number): void => {
    setEmails(emails.filter((_, idx) => idx !== i));
  };

  const save = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      const result = await api.setSnapshotRecipients(event.id, emails);
      setEmails(result.data.map((r) => r.email));
      setSavedAt(Date.now());
      // Re-run the loader so the dirty-diff baseline reflects the persisted
      // list. Without this, `dirty` would stay true until the next mount.
      initial.reload();
    } catch (err) {
      setError(isApiError(err) ? err.message : "Could not save recipients.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Snapshot recipients</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 pt-0 text-sm">
        <p className="text-muted-foreground">
          When an operator clicks the <strong>Snapshot</strong> button on the
          monitor board, a PDF report of the current room state is emailed
          to every address on this list. Recipients are per-event — set
          different lists on different events.
        </p>
        {initial.loading && !hydrated ? (
          <p className="text-muted-foreground">Loading…</p>
        ) : (
          <>
            <ul
              className="space-y-1.5"
              data-testid="snapshot-recipients-list"
            >
              {emails.length === 0 ? (
                <li className="text-muted-foreground">
                  No recipients configured. The Snapshot button will succeed
                  but send no emails until you add at least one address.
                </li>
              ) : null}
              {emails.map((email, i) => (
                <li
                  key={`${email}-${String(i)}`}
                  className="flex items-center justify-between gap-2 rounded border border-border bg-card px-3 py-1.5"
                >
                  <span className="break-all font-mono text-xs">{email}</span>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => removeAt(i)}
                    disabled={saving}
                    data-testid={`snapshot-recipient-remove-${String(i)}`}
                  >
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
            <div className="flex items-center gap-2">
              <Input
                type="email"
                placeholder="name@example.com"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addDraft();
                  }
                }}
                disabled={saving}
                data-testid="snapshot-recipient-input"
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={addDraft}
                disabled={!draft.trim() || saving}
                data-testid="snapshot-recipient-add"
              >
                Add
              </Button>
            </div>
            {error ? (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            ) : null}
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                {savedAt && !dirty ? "Saved." : ""}
              </span>
              <Button
                type="button"
                size="sm"
                onClick={() => {
                  void save();
                }}
                disabled={!dirty || saving}
                data-testid="snapshot-recipients-save"
              >
                {saving ? "Saving…" : "Save recipients"}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
